import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';

const ATELIER_IMG = 'https://images.pexels.com/photos/4614227/pexels-photo-4614227.jpeg?auto=compress&cs=tinysrgb&w=900&q=85';

const useCounter = (end, duration, active) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!active) return;
    let startTime = null;
    const tick = (ts) => {
      if (!startTime) startTime = ts;
      const elapsed = ts - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * end));
      if (progress < 1) requestAnimationFrame(tick);
      else setCount(end);
    };
    const raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active, end, duration]);
  return count;
};

const StatItem = ({ value, suffix, label }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.5 });
  const count = useCounter(value, 1600, inView);
  return (
    <div ref={ref} className="flex flex-col items-center text-center" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <span className="font-cormorant text-3xl md:text-4xl font-semibold text-[#2A2A2A]">
        {value > 100 ? count.toLocaleString('de-DE') : count}{suffix}
      </span>
      <span className="font-outfit text-xs tracking-[0.15em] uppercase text-[#8B7355] mt-1">{label}</span>
    </div>
  );
};

const FamilyStory = () => (
  <section id="ueber-uns" data-testid="family-story-section" className="py-28 bg-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Section header */}
      <motion.div
        className="text-center mb-20"
        initial={{ opacity: 0, y: 28 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.7 }}
      >
        <span className="text-[11px] tracking-[0.22em] uppercase text-[#8B7355] font-outfit mb-3 block">
          Unsere Geschichte
        </span>
        <h2 className="font-cormorant text-4xl md:text-5xl text-[#2A2A2A] font-medium">
          Ein Familienunternehmen. Vier Herzen. Eine Mission.
        </h2>
      </motion.div>

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-center mb-20">
        {/* Text */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8 }}
        >
          <p className="font-outfit text-base text-[#2A2A2A]/75 leading-relaxed mb-6">
            Monika Schulz gründete das Atelier in Heiligensee, An der Schneise 57 — mit Leidenschaft für Stoffe und dem Wunsch, Berliner Wohnräume zu verwandeln. 1988 verstärkte Raumausstatter <strong className="text-[#2A2A2A]">Frank Gojowy</strong> den Betrieb, 1994 erwarb er seinen Meisterbrief.
          </p>

          <div className="bg-[#F8F5F0] border-l-2 border-[#C4944A] px-6 py-5 mb-6">
            <p className="font-outfit text-sm text-[#2A2A2A]/80 italic leading-relaxed">
              "Christiane Wagner (geb. Schulz) schloss 1990 als <strong className="not-italic text-[#8B7355]">erste weibliche Raumausstatter-Auszubildende beim damaligen SFB</strong> ihre Lehre ab — und schrieb damit Berliner Handwerksgeschichte."
            </p>
          </div>

          <p className="font-outfit text-base text-[#2A2A2A]/75 leading-relaxed mb-6">
            Inzwischen gehört auch die Schwiegertochter zum Team. Vier Familienmitglieder, drei Generationen, 35 Jahre Berlin.
          </p>

          <div className="bg-[#F8F5F0] border-l-2 border-[#8B7355] px-6 py-5 mb-8">
            <p className="font-outfit text-sm text-[#2A2A2A]/80 italic leading-relaxed">
              "Am Abendbrottisch entstehen unsere besten Ideen — das ist Familienunternehmen im echten Sinn."
            </p>
          </div>

          <p className="font-outfit text-base text-[#2A2A2A]/75 leading-relaxed">
            Als <strong className="text-[#2A2A2A]">Meisterbetrieb und Ausbildungsbetrieb</strong> der Raumausstatter-Innung Berlin stehen wir für Qualität, die man sieht — und für Handwerk, das bleibt.
          </p>
        </motion.div>

        {/* Image */}
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8 }}
          className="relative"
          data-testid="family-story-image"
        >
          <div className="aspect-[4/5] overflow-hidden">
            <img
              src={ATELIER_IMG}
              alt="Im Gardinen-Atelier Schulz & Gojowy"
              className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
            />
          </div>
          {/* Decorative frame */}
          <div className="absolute -bottom-4 -right-4 w-full h-full border border-[#EDE8E0] -z-10" />
          {/* Badge overlay */}
          <div className="absolute top-6 -left-4 bg-[#8B7355] text-white px-5 py-3">
            <span className="font-cormorant text-lg font-semibold">Meisterbetrieb</span>
            <span className="block font-outfit text-[10px] tracking-[0.15em] uppercase text-white/75 mt-0.5">
              Handwerkskammer Berlin
            </span>
          </div>
        </motion.div>
      </div>

      {/* Stats bar */}
      <motion.div
        className="border-t border-[#EDE8E0] pt-14"
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.7 }}
      >
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-8">
          <StatItem value={1988} suffix="" label="Gegründet" />
          <StatItem value={10200} suffix="+" label="Kunden beraten" />
          <StatItem value={35} suffix="+" label="Jahre Berlin" />
          <StatItem value={4} suffix="" label="Familienmitglieder" />
          <div className="flex flex-col items-center text-center col-span-2 sm:col-span-1">
            <span className="font-cormorant text-3xl md:text-4xl font-semibold text-[#2A2A2A]">
              3
            </span>
            <span className="font-outfit text-xs tracking-[0.15em] uppercase text-[#8B7355] mt-1">
              Generationen
            </span>
          </div>
        </div>
      </motion.div>
    </div>
  </section>
);

export default FamilyStory;
